import React, { useState } from "react";
import { GoogleLogin } from '@react-oauth/google';
import { handleGoogleLogin } from "@/services/googleAuthService";
import { useNavigate } from "react-router-dom";
import PHLogo from "@/Images/Logos/PHLogo_White.png";
import PHIcon from "@/Images/PharmacyHalo_Yellow.ico";
import { MessageComponent } from "@syncfusion/ej2-react-notifications";
import SignUpModal from "@/components/modals/SignUpModal";

const Login = ({ setIsAuthenticated }) => {
  const [globalError, setGlobalError] = useState("");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showUserNotExistModal, setShowUserNotExistModal] = useState(false);
  const navigate = useNavigate();

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsGoogleLoading(true);
    try {
      const result = await handleGoogleLogin(credentialResponse, setIsAuthenticated, navigate, () => {});
      
      if (result.needsLinking) {
        // User doesn't exist - show modal popup
        setShowUserNotExistModal(true);
      }
      // If no linking needed, user is already logged in and redirected
    } catch (error) {
      setGlobalError("Google authentication failed. Please try again.");
      console.error("Google login error:", error);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setGlobalError("Google authentication failed. Please try again.");
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Section */}
      <div className="flex-1 flex flex-col justify-center items-center bg-gray-100">
        {/* Notification Area */}
        {globalError && (
          <div className="w-full max-w-md mb-4">
            <MessageComponent
              severity="Error"
              content={globalError}
              showCloseIcon={true}
              onClose={() => setGlobalError("")}
            />
          </div>
        )}

        {/* Login Container */}
        <div className="bg-white shadow-lg rounded-xl p-8 w-full max-w-md">
          <img
            src={PHIcon}
            alt="Pharmacy Halo Icon"
            className="mx-auto mb-4 w-12 h-12 rounded-xl border border-gray-300"
          />
          <h1 className="text-3xl font-bold mt-2 mb-2 text-center text-primary">
            Head Office Portal
          </h1>
          <p className="text-lg text-gray-600 mb-8 text-center">
            Please Sign in
          </p>

          {/* Google Sign-In Button */}
          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              disabled={isGoogleLoading}
              theme="outline"
              size="large"
              text="signin_with"
              shape="rectangular"
            />
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Don't have an account?</span>
            </div>
          </div>

          {/* Sign Up Button */}
          <div className="flex justify-center">
            <button
              onClick={() => setShowSignUp(true)}
              className="w-full bg-secondary text-white py-2 px-4 rounded-lg hover:bg-opacity-90 transition duration-300 font-semibold"
            >
              Sign Up
            </button>
          </div>
        </div>
      </div>

      {/* Right Section */}
      <div className="flex-1 hidden md:flex justify-center items-center bg-primary">
        <img src={PHLogo} alt="PH Logo" className="w-96 h-auto" />
      </div>

      {/* Sign Up Modal */}
      <SignUpModal
        isOpen={showSignUp}
        onClose={() => setShowSignUp(false)}
        setIsAuthenticated={setIsAuthenticated}
        navigate={navigate}
      />

      {/* User Not Exist Modal */}
      {showUserNotExistModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md mx-4 relative">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold mb-2 text-center text-gray-900">
              User Doesn't Exist
            </h2>

            {/* Message */}
            <p className="text-gray-600 text-center mb-6">
              This Google account is not registered in our system. Please sign up to create a new account or contact Pharmacy Halo for assistance.
            </p>

            {/* Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowUserNotExistModal(false);
                  setShowSignUp(true);
                }}
                className="w-full bg-primary text-white py-3 px-4 rounded-lg hover:bg-opacity-90 transition duration-300 font-semibold"
              >
                Sign Up Now
              </button>
              <button
                onClick={() => setShowUserNotExistModal(false)}
                className="w-full bg-gray-200 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-300 transition duration-300 font-semibold"
              >
                Close
              </button>
            </div>

            {/* Contact Info */}
            <p className="text-sm text-gray-500 text-center mt-4">
              Need help? Contact Pharmacy Halo support
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
